import type { Receipt } from '../types';

/** Strip trailing punctuation often glued by OCR */
function trimTrail(s: string): string {
  return s.replace(/[.,;:!?)\]'"\u201d\u2019]+$/g, '').trim();
}

function dedupeCaseInsensitive(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const k = v.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
}

/**
 * Extract email addresses (must contain @) from OCR / PDF text.
 */
export function extractEmailsFromText(text: string): string[] {
  if (!text?.trim()) return [];
  const re = /\b[A-Z0-9](?:[A-Z0-9._%+-]*[A-Z0-9])?@[A-Z0-9](?:[A-Z0-9.-]*[A-Z0-9])?\.[A-Z]{2,}\b/gi;
  const found: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const t = trimTrail(m[0]);
    if (t.includes('@') && t.length > 5) found.push(t);
  }
  return dedupeCaseInsensitive(found);
}

/**
 * Extract web addresses: http(s) URLs and hostnames starting with www.
 */
export function extractWebsitesFromText(text: string): string[] {
  if (!text?.trim()) return [];
  const found: string[] = [];

  const httpRe = /\bhttps?:\/\/[^\s<>"')\]]+/gi;
  let m: RegExpExecArray | null;
  while ((m = httpRe.exec(text)) !== null) {
    const t = trimTrail(m[0]);
    if (t.length > 8) found.push(t);
  }

  const wwwRe = /\bwww\.[^\s<>"')\]]+/gi;
  while ((m = wwwRe.exec(text)) !== null) {
    const t = trimTrail(m[0]);
    if (t.length > 6) found.push(t);
  }

  return dedupeCaseInsensitive(found);
}

export function extractContactsFromText(text: string): { emails: string[]; websites: string[] } {
  return {
    emails: extractEmailsFromText(text),
    websites: extractWebsitesFromText(text),
  };
}

/** Open / mailto target */
export function websiteHref(display: string): string {
  const t = display.trim();
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

/**
 * Prefer stored contacts; otherwise scan the excerpt (older receipts).
 */
export function getReceiptContactsForDisplay(r: Receipt): { emails: string[]; websites: string[] } {
  if ((r.contact_emails?.length ?? 0) > 0 || (r.contact_websites?.length ?? 0) > 0) {
    return {
      emails: r.contact_emails ?? [],
      websites: r.contact_websites ?? [],
    };
  }
  return extractContactsFromText(r.raw_text_excerpt || '');
}
